import { shopService } from "@cabukaka/services";
import { Sector, Shop } from "@cabukaka/types";
import { useEffect, useState } from "react";
import { Store, ChevronRight } from "lucide-react";
import { NavLink } from "../../ui/NavLink";
import { ShopCard } from "./ShopCard";

interface ShopsListBySectorProps {
    sector: Sector;
}

export default function ShopsListBySector({ sector }: ShopsListBySectorProps) {
    const [shops, setShops] = useState<Shop[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getShops = async () => {
            try {
                setLoading(true);
                const res = await shopService.getShopsBySectorSlug(sector.slug);
                setShops(res.data || []);
            } catch (error) {
                console.error("Erreur chargement boutiques:", error);
            } finally {
                setLoading(false);
            }
        };
        getShops();
    }, [sector.slug]);

    if (loading) {
        return (
            <section className="py-6 md:py-8">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 w-48 bg-gray-200 rounded-lg"></div>
                    <div className="flex gap-4 overflow-hidden">
                        <div className="h-64 min-w-[280px] bg-gray-100 rounded-2xl"></div>
                        <div className="h-64 min-w-[280px] bg-gray-100 rounded-2xl"></div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-6 md:py-8">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 mb-6 md:mb-8 px-1">
                {/* Conteneur du titre : min-w-0 permet au texte de se tronquer au lieu de pousser les autres éléments */}
                <div className="flex items-center gap-4 min-w-0">
                  
                    <div className="min-w-0">
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight truncate">
                            {sector.name}
                        </h2>
                        <p className="text-sm text-gray-500 font-medium truncate">
                            {shops.length} {shops.length > 1 ? "boutiques disponibles" : "boutique disponible"}
                        </p>
                    </div>
                </div>

                {/* shrink-0 empêche le bouton de réduire sa taille si le titre est long */}
                <NavLink
                    to={`/boutiques/secteur/${sector.slug}`}
                    className="flex-shrink-0 flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                    Voir plus
                    <ChevronRight className="w-4 h-4" />
                </NavLink>
            </div>

            {/* Conteneur scrollable avec largeur restreinte */}
            {shops.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-100">
                    <Store className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucune boutique trouvée dans ce secteur</p>
                </div>
            ) : (
                <div className="overflow-x-auto pb-8 -mb-8 px-1 scrollbar-hide">
                    {/* w-fit empêche l'étirement, flex assure l'alignement */}
                    <div className="flex gap-4 w-fit">
                        {shops.map((shop) => (
                            <div
                                key={shop.id}
                                className="w-[280px] md:w-[320px] shrink-0"
                            >
                                <ShopCard
                                    shop={shop}
                                    showRating={true}
                                    showLocation={true}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}